// Fetches root.json for a video, hashes the bytes exactly as received, and
// compares that hash against the chain's root_hash. This is the link the
// whole product rests on: docs/plans/2026-08-13-verification-integration-closeout.md,
// Decision 1 — the API only ever tells a client WHERE to look
// (GET /api/v1/videos/:id/verification/root.json is public, unauthenticated,
// and self-defeating to lie about, per that decision); the chain's root_hash
// is what's actually trusted.
import { API_BASE_URL } from '../constants.js'
import { hexEqual, sha256Hex } from './hash.js'

// Hand-mirrored from api/pkg/verification/v2/manifest.go's RootManifest.
// ./stack generate_types does not cover this — it only regenerates
// frontend/src/types.ts from api/pkg/types, and this struct lives in
// api/pkg/verification/v2 — so these fields must be kept in sync by hand
// with the Go source if it changes. Field names stay snake_case on purpose:
// this is a straight JSON.parse of the wire bytes (see the verbatim-bytes
// rule below), not a decoded/renamed value, so the keys are exactly what the
// server wrote.
export interface RenditionRef {
  name: string
  resolution: string
  codecs: string
  bandwidth: number
  manifest_path: string
  manifest_hash: string
  segment_count: number
}

export interface SubtitleRef {
  language: string
  is_master: boolean
  path: string
  hash: string
  cue_count: number
}

export interface Participant {
  id: string
  name: string
  color: string
  position: number
  avatar_path: string
  avatar_hash: string
}

export interface Attribution {
  cue: number
  participant: string
}

export interface Content {
  title: string
  description: string
  language: string
  rotation_adjustment: number
  subtitle_offset: number
  participants: Participant[]
  speaker_attributions: Attribution[]
}

export interface SourceInfo {
  provider: string
  observed_at: string
}

export interface RootManifest {
  schema: string
  hash_algo: string
  video_id: string
  published_at: string
  revision: number
  master_playlist_path: string
  source: SourceInfo
  content: Content
  thumbnail_path: string
  thumbnail_hash: string
  subtitles: SubtitleRef[]
  renditions: RenditionRef[]
}

export interface RootVerified {
  ok: true
  manifest: RootManifest
  /** The computed hash, lowercase hex. Equal to the expected hash by
   * construction (case-insensitively) — kept on the result so a caller
   * never has to recompute it to display or log it. */
  hash: string
}

export interface RootVerificationFailure {
  ok: false
  stage: 'root'
  /**
   * Distinguishes "could not obtain trustworthy bytes to compare" from
   * "obtained bytes, and they hash differently than what the chain sealed".
   * Decision 4 of the closeout plan depends on these never being conflated:
   * a later ticket stops playback on a 'mismatch' but must NOT stop it on a
   * 'network' failure (a CDN blip, a 5xx, offline). The distinction lives in
   * this field, not merely in `detail`'s wording, so a caller can switch on
   * it instead of matching strings.
   */
  kind: 'network' | 'mismatch'
  detail: string
  /** Present only when kind === 'mismatch'. */
  expectedHash?: string
  /** Present only when kind === 'mismatch'. */
  actualHash?: string
}

export type VerifyRootResult = RootVerified | RootVerificationFailure

/**
 * Never throws into the render path — every failure, from a rejected fetch
 * to a non-2xx response to a hash mismatch, comes back as a structured
 * `RootVerificationFailure` with `stage: 'root'`.
 */
export async function verifyRoot(videoId: string, expectedRootHash: string): Promise<VerifyRootResult> {
  let bytes: ArrayBuffer
  try {
    const response = await fetch(`${API_BASE_URL}/videos/${videoId}/verification/root.json`)
    if (!response.ok) {
      return {
        ok: false,
        stage: 'root',
        kind: 'network',
        detail: `root.json request failed: HTTP ${response.status}`,
      }
    }
    // The ArrayBuffer IS the artifact (docs/plans/2026-08-10-verification-manifest.md
    // §6: "the stored file is the artifact. Never re-serialize it."). This is
    // hashed below BEFORE anything decodes or JSON.parses it, so the hash
    // always covers exactly the bytes the server sent — never a re-encoded
    // copy, which would silently produce a different, wrong digest.
    bytes = await response.arrayBuffer()
  } catch (err) {
    return {
      ok: false,
      stage: 'root',
      kind: 'network',
      detail: err instanceof Error ? err.message : 'Could not reach the API',
    }
  }

  const actualHash = await sha256Hex(bytes)
  if (!hexEqual(actualHash, expectedRootHash)) {
    return {
      ok: false,
      stage: 'root',
      kind: 'mismatch',
      detail: 'root.json does not match the hash sealed on chain',
      expectedHash: expectedRootHash,
      actualHash,
    }
  }

  // Parsing happens last, and only for consumption by later verification
  // stages (T14 reads .renditions / .subtitles off this) — the hash compare
  // above has already run against the untouched bytes, so a parse failure
  // here can never be mistaken for (or mask) a mismatch.
  let manifest: RootManifest
  try {
    manifest = JSON.parse(new TextDecoder().decode(bytes)) as RootManifest
  } catch {
    // The bytes matched the chain's hash but are not valid JSON. Per the
    // manifest design's canonical-bytes rule this should be impossible if
    // the server wrote what it hashed, so this says something is wrong
    // upstream — it is not evidence the seal itself was broken, hence
    // 'network' rather than 'mismatch'.
    return {
      ok: false,
      stage: 'root',
      kind: 'network',
      detail: 'root.json matched its on-chain hash but could not be parsed as JSON',
    }
  }

  return { ok: true, manifest, hash: actualHash }
}
